import { Box, CircularProgress, makeStyles, Typography } from "@material-ui/core";
import * as React from "react";

import { BottomSheet } from "@/components/BottomSheet";
import { CustomButton } from "@/components/Button";
import { CustomChip } from "@/components/Chip";
import theme from "@/theme";

const MIN_SELECTABLE = 2;
const MAX_SELECTABLE = 5;
const SELECTION_ERROR_MESSAGE = "2〜5個の範囲で選んでください";
const TAGS_UNAVAILABLE_MESSAGE = "条件を読み込めませんでした。";
const DESCRIPTION = "認定プランナーに求める条件を選択してください。（2〜5個まで）";
const useStyles = makeStyles(() => ({
  panel: {
    backgroundColor: theme.palette.semantic["surface/card"],
    borderRadius: 16,
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  head: { display: "flex", flexDirection: "column", gap: 4 },
  title: { fontSize: 16, fontWeight: 700, lineHeight: 1.3, color: theme.palette.semantic["text/heading"] },
  description: { fontSize: 14, lineHeight: 1.5, color: theme.palette.text.secondary },
  body: { display: "flex", flexDirection: "column", gap: 16, padding: "8px 0" },
  chips: { display: "flex", flexWrap: "wrap", gap: 8 },
  error: { fontSize: 12, lineHeight: 1.5, color: theme.palette.error.main },
  state: { display: "flex", flexDirection: "column", gap: 16, alignItems: "center" },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    alignItems: "center",
    width: "100%",
    "& button": { [theme.breakpoints.up("md")]: { width: 240 } },
  },
}));

interface SearchModalProps {
  open: boolean;
  inline?: boolean;
  onReset?: () => void;
  onClose: () => void;
  onSearch: (tagIds: number[]) => void;
  selectedConditions: number[];
  // マスタタグはここでは取得せず、一覧側（index.tsx）の1インスタンスから受け取る。
  // モーダルでも取得すると同じフックのインスタンスが2つになり、モーダルで再試行して成功しても
  // 一覧側の辞書（条件バーのラベル用）は空のままになるため、取得元を1つに集約している
  tags: Array<{ id: number; name: string; order?: number | null }>;
  isTagsLoading: boolean;
  onRetryTags: () => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({
  open,
  inline = false,
  onReset,
  onClose,
  onSearch,
  selectedConditions,
  tags: masterTags,
  isTagsLoading: masterTagsLoading,
  onRetryTags,
}) => {
  const classes = useStyles();
  // 取得が終わってもタグが0件なら選択自体ができないため、検索ボタンを押しても件数エラーしか出せない
  const isTagsUnavailable = !masterTagsLoading && masterTags.length === 0;

  const [selected, setSelected] = React.useState<number[]>([]);
  const [error, setError] = React.useState<string>("");

  // 開くたびに一覧側の確定条件へ戻す（閉じてキャンセルした選択を持ち越さない）
  React.useEffect(() => {
    if (!open) return;
    setSelected(selectedConditions);
    setError("");
  }, [open, selectedConditions]);

  const toggleCondition = (tagId: number) => {
    setSelected((prev) => {
      const isSelected = prev.includes(tagId);
      const newSelected = isSelected ? prev.filter((id) => id !== tagId) : [...prev, tagId];

      if (newSelected.length > MAX_SELECTABLE) {
        setError(SELECTION_ERROR_MESSAGE);
        return prev;
      }
      setError("");
      return newSelected;
    });
  };

  const handleSearch = () => {
    if (selected.length < MIN_SELECTABLE) {
      setError(SELECTION_ERROR_MESSAGE);
      return;
    }
    onSearch(selected);
    onClose();
  };

  if (!open) return null;

  const actions = (
    <Box className={classes.actions}>
      <CustomButton
        tone="primary"
        buttonSize="M"
        fullWidth
        onClick={handleSearch}
        disabled={isTagsUnavailable || masterTagsLoading}
      >
        この条件で検索する
      </CustomButton>
      {onReset && inline && (
        <CustomButton
          tone="text"
          buttonSize="M"
          onClick={() => {
            setSelected([]);
            setError("");
            onReset();
          }}
        >
          条件をリセットする
        </CustomButton>
      )}
      {!inline && (
        <CustomButton tone="text" buttonSize="M" fullWidth onClick={onClose}>
          やめる
        </CustomButton>
      )}
    </Box>
  );
  const body = (
    <Box className={classes.body}>
      {masterTagsLoading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress size={40} />
        </Box>
      ) : isTagsUnavailable ? (
        <Box className={classes.state}>
          <Typography className={classes.description}>{TAGS_UNAVAILABLE_MESSAGE}</Typography>
          <CustomButton tone="outline" buttonSize="M" onClick={onRetryTags}>
            再試行する
          </CustomButton>
        </Box>
      ) : (
        <Box className={classes.chips}>
          {masterTags.map(({ id, name }) => (
            <CustomChip
              key={id}
              label={name}
              outlined
              clickable
              selected={selected.includes(Number(id))}
              onClick={() => toggleCondition(Number(id))}
            />
          ))}
        </Box>
      )}
      {error && (
        <Typography role="alert" className={classes.error}>
          {error}
        </Typography>
      )}
    </Box>
  );
  return inline ? (
    <Box className={classes.panel} data-testid="planner-search-panel">
      <Box className={classes.head}>
        <Typography component="h2" className={classes.title}>
          条件で絞り込む
        </Typography>
        <Typography className={classes.description}>{DESCRIPTION}</Typography>
      </Box>
      {body}
      {actions}
    </Box>
  ) : (
    <BottomSheet
      open={open}
      onOpen={() => undefined}
      onClose={onClose}
      title="詳細条件から探す"
      description={<span className={classes.description}>{DESCRIPTION}</span>}
      footer={actions}
    >
      {body}
    </BottomSheet>
  );
};
